import React, { useState, useEffect, useMemo } from "react";
import Chart from "react-apexcharts";
import { fetchStockChartsData } from "../../apis/Stock";
import useDarkMode from "../../hooks/useDarkMode";
import { getChartTheme } from "../../utils/chartTheme";

export default function StockLineChart13F({ ticker, companykor, onDataLoad, chartSelection }) {
  const [chartSource, setChartSource] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const isDarkMode = useDarkMode();
  const chartColors = useMemo(() => getChartTheme(isDarkMode), [isDarkMode]);

  const stockStyle = isDarkMode
    ? { line: "#34d399", area: "#34d399" }
    : { line: "#16a34a", area: "#16a34a" };

  const holdingsStyle = { line: "rgba(255, 99, 132, 0.8)", area: "rgba(255, 99, 132, 0.1)" };

  // Calculate 3 year range for initial view
  const threeYearRange = useMemo(() => {
    if (chartSource.length === 0) return { min: undefined, max: undefined };
    const firstEntry = chartSource[0];
    const lastEntry = chartSource[chartSource.length - 1];
    if (!lastEntry || !lastEntry.date || !firstEntry || !firstEntry.date) return { min: undefined, max: undefined };

    const latest = new Date(lastEntry.date);
    const start = new Date(firstEntry.date);

    const threeYearsAgo = new Date(latest);
    threeYearsAgo.setFullYear(latest.getFullYear() - 3);

    // Use the later of 3 years ago or the actual start of data
    const finalMin = Math.max(threeYearsAgo.getTime(), start.getTime());

    return {
      min: finalMin,
      max: latest.getTime()
    };
  }, [chartSource]);

  // 주가 데이터 가져오기
  useEffect(() => {
    if (!ticker) return;
    setIsLoading(true);
    fetchStockChartsData(ticker)
      .then((res) => res.json())
      .then((data) => {
        data.sort((a, b) => new Date(a.date) - new Date(b.date));
        setChartSource(data);
        if (onDataLoad) {
          onDataLoad(data.map(entry => entry.date));
        }
      })
      .catch((err) => console.error("Error fetching line chart (13F):", err))
      .finally(() => setIsLoading(false));
  }, [ticker, onDataLoad]);

  // 보유 수량 데이터 가져오기
  const holdingsSeriesData = useMemo(() => {
    if (!chartSelection || !chartSelection.selectedTrades) return [];

    // Sort trades by date
    const sortedTrades = [...chartSelection.selectedTrades].sort((a, b) =>
      new Date(a.date).getTime() - new Date(b.date).getTime()
    );

    return sortedTrades.map(trade => ({
      x: new Date(trade.date).getTime(),
      y: trade.numshare || 0
    }));
  }, [chartSelection]);

  const series = useMemo(() => {
    const list = [{
      name: `${companykor} 주가`,
      type: "area",
      data: chartSource.map((entry) => ({
        x: new Date(entry.date).getTime(),
        y: entry.close
      }))
    }];

    if (holdingsSeriesData.length > 0) {
      list.push({
        name: `${chartSelection.selectedName} 보유 수량`,
        type: "line",
        data: holdingsSeriesData
      });
    }

    return list;
  }, [chartSource, companykor, holdingsSeriesData, chartSelection]);

  const mainOptions = {
    chart: {
      id: `chart-13f-${ticker}`,
      toolbar: { show: false },
      zoom: { enabled: false },
      animations: { enabled: false },
      background: 'transparent',
    },
    colors: [stockStyle.line, holdingsStyle.line],
    fill: {
      type: ['gradient', 'solid'],
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.45,
        opacityTo: 0.05,
        stops: [0, 100]
      }
    },
    stroke: {
      curve: "smooth",
      width: [2, 3],
      dashArray: [0, 0]
    },
    dataLabels: { enabled: false },
    markers: {
      size: [0.1, 5],
      hover: { size: 7 },
      strokeWidth: [0, 2],
      strokeColors: isDarkMode ? "#1f2937" : "#fff"
    },
    xaxis: {
      type: "datetime",
      labels: {
        style: { colors: chartColors.tickColor, fontSize: '11px' },
        format: 'yy.MM',
      },
      offsetY: -10,
      axisBorder: { show: false },
      axisTicks: { show: false },
      tooltip: { enabled: false },
      tickAmount: 8,
      min: threeYearRange.min,
      max: threeYearRange.max,
    },
    yaxis: [
      {
        title: { style: { color: stockStyle.line, fontSize: '11px' } },
        labels: {
          style: { colors: chartColors.tickColor, fontSize: '11px' },
          formatter: (val) => val >= 1000 ? val.toLocaleString() : val.toFixed(1),
          offsetX: -10
        }
      },
      {
        opposite: true,
        show: holdingsSeriesData.length > 0,
        min: 0,
        title: { style: { color: holdingsStyle.line, fontSize: '11px' } },
        labels: {
          style: { colors: chartColors.tickColor, fontSize: '11px' },
          offsetX: -15,
          formatter: (val) => {
            if (val === 0 && holdingsSeriesData.length === 0) return "";
            if (val >= 1_000_000_000) return (val / 1_000_000_000).toFixed(1) + "B";
            if (val >= 1_000_000) return (val / 1_000_000).toFixed(1) + "M";
            if (val >= 1_000) return (val / 1_000).toFixed(1) + "K";
            return val.toFixed(1);
          }
        },
      }
    ],
    grid: {
      borderColor: chartColors.gridColor,
      strokeDashArray: 4,
      padding: {
        top: 0,
        bottom: 0,
        left: 5,
        right: -10
      }
    },
    tooltip: {
      shared: true,
      hideEmptySeries: true,
      intersect: false,
      followCursor: true,
      theme: isDarkMode ? "dark" : "light",
      x: { format: "yyyy.MM.dd" },
      y: {
        formatter: (val, { seriesIndex }) => {
          if (seriesIndex === 0) return `$${val.toLocaleString()}`;
          if (val >= 1_000_000_000) return (val / 1_000_000_000).toFixed(1) + "B";
          if (val >= 1_000_000) return (val / 1_000_000).toFixed(1) + "M";
          if (val >= 1_000) return (val / 1_000).toFixed(1) + "K";
          return val.toFixed(1);
        }
      }
    },
    legend: {
      show: true,
      position: 'top',
      horizontalAlign: 'center',
      labels: { colors: chartColors.legendColor },
    },
  };

  if (isLoading) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center h-100 text-muted" style={{ minHeight: "300px" }}>
        <div className="spinner-border spinner-border-sm text-primary mb-2" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <div style={{ fontSize: "13px" }}>데이터를 불러오는 중입니다...</div>
      </div>
    );
  }

  if (chartSource.length === 0) {
    return (
      <div className="d-flex align-items-center justify-content-center text-muted border border-secondary border-dashed h-100" style={{ borderStyle: "dashed", minHeight: "300px" }}>
        데이터가 없습니다.
      </div>
    );
  }

  return (
    <div className="w-100" style={{ minHeight: "300px" }}>
      <Chart
        options={mainOptions}
        series={series}
        type="line"
        height="300"
      />
    </div>
  );
}
